import React, { useState, useEffect } from 'react';
import { ExclamationCircleOutlined, CloseOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

type ActionType = 'suspend' | 'stop' | 'delete' | 'mark_paid' | 'custom';
type TargetType = 'client' | 'service' | 'payment';

interface TargetInfo {
  type: TargetType;
  name: string;
  details?: string;
  itemCount?: number;
}

interface ConfirmActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void | Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'warning' | 'danger';
  action?: ActionType;
  target?: TargetInfo;
  impact?: string;
  impactMessage?: string;
  reasonPlaceholder?: string;
  showReasonField?: boolean;
  showConsequenceCheckbox?: boolean;
  enableUndo?: boolean;
  undoSeconds?: number;
  loading?: boolean;
  onReasonChange?: (reason: string) => void;
  onError?: (error: Error) => void;
}

interface ProcessingState {
  status: 'idle' | 'processing' | 'success' | 'error';
  message?: string;
}

const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  action = 'custom',
  target,
  impact,
  impactMessage,
  reasonPlaceholder = 'Enter reason (optional)',
  showReasonField = false,
  showConsequenceCheckbox = true,
  enableUndo = false,
  undoSeconds = 10,
  loading = false,
  onReasonChange,
  onError,
}) => {
  const [reason, setReason] = useState('');
  const [understands, setUnderstands] = useState(false);
  const [processingState, setProcessingState] = useState<ProcessingState>({ status: 'idle' });
  const [undoCountdown, setUndoCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Undo timer
  useEffect(() => {
    if (undoCountdown > 0) {
      const timer = setTimeout(() => {
        setUndoCountdown(undoCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (enableUndo && processingState.status === 'success' && undoCountdown === 0) {
      // Auto-close after undo window expired
      const timer = setTimeout(() => {
        handleClose();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [undoCountdown, processingState.status, enableUndo]);

  if (!isOpen) return null;

  const handleReason = (value: string) => {
    setReason(value);
    onReasonChange?.(value);
  };

  const handleConfirm = async () => {
    setError(null);
    setProcessingState({ status: 'processing', message: 'Procesando...' });
    try {
      const result = onConfirm(showReasonField ? reason : undefined);
      if (result instanceof Promise) {
        await result;
      }
      setProcessingState({
        status: 'success',
        message: 'Operación completada exitosamente'
      });
      if (enableUndo) {
        setUndoCountdown(undoSeconds);
      } else {
        setTimeout(() => handleClose(), 1500);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMsg);
      setProcessingState({
        status: 'error',
        message: `Error: ${errorMsg}`
      });
      onError?.(err instanceof Error ? err : new Error(errorMsg));
    }
  };

  const handleClose = () => {
    setReason('');
    setUnderstands(false);
    setProcessingState({ status: 'idle' });
    setUndoCountdown(0);
    setError(null);
    onClose();
  };

  const variantConfig = {
    default: {
      icon: 'text-blue-600 dark:text-blue-400',
      button: 'bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 text-white',
      bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
    },
    warning: {
      icon: 'text-amber-600 dark:text-amber-400',
      button: 'bg-amber-600 hover:bg-amber-700 dark:hover:bg-amber-700 text-white',
      bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200',
    },
    danger: {
      icon: 'text-red-600 dark:text-red-400',
      button: 'bg-red-600 hover:bg-red-700 dark:hover:bg-red-700 text-white',
      bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
    },
  };

  const config = variantConfig[variant];

  const getActionDefaultImpact = (): string | undefined => {
    if (impact) return impact;
    if (!target) return undefined;

    switch (action) {
      case 'suspend':
        return target.itemCount
          ? `Esto detendrá ${target.itemCount} servicio${target.itemCount > 1 ? 's' : ''} activo${target.itemCount > 1 ? 's' : ''}`
          : `El cliente "${target.name}" será suspendido inmediatamente`;
      case 'delete':
        return `No se puede deshacer. ${target.type === 'client' ? 'El cliente' : 'El servicio'} "${target.name}" y todos sus datos serán eliminados permanentemente`;
      case 'stop':
        return `${target.type === 'service' ? 'El servicio' : 'La instancia'} "${target.name}" será detenido${target.details ? ` (${target.details})` : ''}`;
      case 'mark_paid':
        return `Se marcará "${target.name}" como pagado en el sistema de auditoría`;
      default:
        return undefined;
    }
  };

  const finalImpact = impactMessage || getActionDefaultImpact();
  const isProcessing = processingState.status === 'processing';
  const isSuccess = processingState.status === 'success';
  const isErrorState = processingState.status === 'error';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={() => !isProcessing && handleClose()}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
            <div className="flex items-start gap-3 flex-1">
              <div className={`flex-shrink-0 ${config.icon} text-2xl`}>
                {isSuccess ? (
                  <CheckCircleOutlined />
                ) : isErrorState ? (
                  <CloseCircleOutlined />
                ) : (
                  <ExclamationCircleOutlined />
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {isSuccess ? 'Operación completada' : isErrorState ? 'Error' : title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {isSuccess || isErrorState ? processingState.message : description}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Content - only show during processing or idle state */}
          {!isSuccess && !isErrorState && (
            <>
              {/* Impact Message */}
              {finalImpact && (
                <div className="px-6 pt-4 pb-2">
                  <div className={`border rounded-lg p-3 ${config.bg}`}>
                    <p className="text-sm">
                      <strong>Impacto:</strong> {finalImpact}
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="px-6 pt-4 pb-2">
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* Consequence Checkbox */}
              {showConsequenceCheckbox && (
                <div className={`px-6 ${finalImpact ? 'pt-3' : 'pt-4'} pb-2`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={understands}
                      onChange={(e) => setUnderstands(e.target.checked)}
                      disabled={isProcessing}
                      className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Entiendo las consecuencias de esta acción
                    </span>
                  </label>
                </div>
              )}

              {/* Reason Field */}
              {showReasonField && (
                <div className={`px-6 ${finalImpact || showConsequenceCheckbox ? 'pt-3' : 'pt-4'} pb-4`}>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Razón (opcional)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => handleReason(e.target.value)}
                    placeholder={reasonPlaceholder}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none disabled:opacity-50"
                    rows={3}
                  />
                </div>
              )}
            </>
          )}

          {/* Undo Timer */}
          {isSuccess && enableUndo && undoCountdown > 0 && (
            <div className="px-6 py-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Puedes cambiar de idea en <strong>{undoCountdown}</strong> segundos
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-xl border-t border-gray-200 dark:border-gray-700 sticky bottom-0">
            {isSuccess || isErrorState ? (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cerrar
              </button>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  disabled={isProcessing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelText}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isProcessing || (showConsequenceCheckbox && !understands)}
                  className={`
                    px-4 py-2 text-sm font-medium rounded-lg transition-colors
                    flex items-center gap-2
                    ${
                      isProcessing || (showConsequenceCheckbox && !understands)
                        ? 'opacity-50 cursor-not-allowed bg-gray-400 text-white'
                        : config.button
                    }
                  `}
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    confirmText
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmActionModal;
